/*
 * Where a conversation's open thread is kept, and where focus goes with it: the rules behind the
 * conversation page's thread, apart from it so they can be checked without a browser.
 *
 * The open thread is the address's `thread` (`/chat/<conversation>?thread=<message>`), so a link
 * opens a conversation at a thread, a reload keeps it, and Back closes it.
 */

/** The search parameter naming the message to land on (`message-place.ts`). */
const landedParam = "message"

/** The search parameter naming the message whose thread is open. */
export const threadParam = "thread"

/** The message whose thread `search` opens, none when it opens none. */
export function threadOf(search: URLSearchParams): string | null {
  return search.get(threadParam) || null
}

/**
 * The address that opens the thread `rootId`: a new step in the history from the conversation
 * alone, so Back closes it; in place of a thread already `open`, so Back still returns to the
 * conversation. `fromConversation` goes with the step, for closing it by Back. A message landed on
 * is let go: it was in the conversation, or in the thread open before.
 */
export function openingThread(search: URLSearchParams, rootId: string, open: boolean) {
  const next = new URLSearchParams(search)
  next.delete(landedParam)
  next.set(threadParam, rootId)
  return { search: next.toString(), replace: open, fromConversation: true }
}

/**
 * How to close the thread `search` opens: by Back when it was opened from the conversation
 * (`fromConversation`), so the history holds no step for it; otherwise (a link, a reload) by the
 * address without it (nor a reply landed on in it), in place, never by leaving the app.
 */
export function closingThread(
  search: URLSearchParams,
  fromConversation: boolean,
): { back: true } | { back: false; search: string } {
  if (fromConversation) return { back: true }
  const next = new URLSearchParams(search)
  next.delete(threadParam)
  next.delete(landedParam)
  return { back: false, search: next.toString() }
}

/**
 * What takes focus as a thread opens: the reply box beside the conversation on the desk, where
 * replying is what comes next, for a Member who may reply; otherwise the thread's heading, so a
 * screen reader starts there and the on-screen keyboard doesn't cover the thread just opened.
 */
export function focusOnOpening({
  layout,
  touch,
  canReply,
}: {
  layout: "side" | "page"
  touch: boolean
  canReply: boolean
}): "reply" | "heading" {
  return layout === "side" && !touch && canReply ? "reply" : "heading"
}
